import { stockModeOf, variantStockMode, comboKey } from "./stockDispatch";

const CART_KEY = "it_cart";

// Weights are stored in grams. Values below 1 were entered as kg (old admin label), so convert.
export const toGrams = (w) => {
  const n = Number(w) || 0;
  return n > 0 && n < 1 ? Math.round(n * 1000) : n;
};

export function getCart() {
  try { return JSON.parse(localStorage.getItem(CART_KEY) || "[]"); }
  catch { return []; }
}

export function saveCart(items) {
  localStorage.setItem(CART_KEY, JSON.stringify(items));
  window.dispatchEvent(new Event("it_cart_updated"));
}

export function addToCart(product, quantities, unitPrice) {
  const cart = getCart();
  const totalQty = Object.values(quantities).reduce((s, q) => s + q, 0);
  if (totalQty === 0) return false;

  // Products that opted into backorder/strict stock keep each variant's stock + weight with the cart line.
  // Variant-level continue_selling_oos takes precedence over the product-level setting.
  const productMode = stockModeOf(product);
  const combos = product.variant_combinations || [];
  const variantLines = Object.entries(quantities)
    .filter(([, q]) => q > 0)
    .map(([key, qty]) => {
      const [color, size] = key.split("|");
      const line = { key, color: color || null, size: size || null, qty };
      const combo = combos.find(c => comboKey(c) === key);
      const varMode = variantStockMode(product, combo);
      line.weight = toGrams(combo?.weight) || 0;   // shipping uses each variant's own weight
      if (varMode) {
        line.stock = typeof combo?.stock === "number" ? combo.stock : null;
        line.stockMode = varMode;   // variant-specific; overrides item.stockMode in checks
      }
      return line;
    });

  const existing = cart.findIndex(i => i.id === product.id);
  const item = {
    id: product.id,
    slug: product.url_slug,
    name: product.item_name,
    image: product.main_image,
    price: unitPrice,
    mrp: product.mrp || 0,
    rating: product.star_rating || 0,
    numReviews: product.num_reviews || 0,
    weight: toGrams(product.shipping_weight),
    variantLines,
    totalQty,
    ...(productMode ? { stockMode: productMode } : {}),  // product-level fallback for backward compat
  };

  if (existing >= 0) cart[existing] = item;
  else cart.push(item);

  saveCart(cart);
  return true;
}

export function removeFromCart(productId) {
  saveCart(getCart().filter(i => i.id !== productId));
}

export function updateLineQty(productId, lineKey, newQty) {
  const cart = getCart().map(item => {
    if (item.id !== productId) return item;
    const variantLines = item.variantLines.map(l =>
      l.key === lineKey ? { ...l, qty: newQty } : l
    ).filter(l => l.qty > 0);
    return { ...item, variantLines, totalQty: variantLines.reduce((s, l) => s + l.qty, 0) };
  }).filter(i => i.totalQty > 0);
  saveCart(cart);
}

export function getCartCount() {
  return getCart().reduce((s, i) => s + i.totalQty, 0);
}

// Weight for a line added without choosing a variant: the card's own variant (e.g. the "Beige" card),
// else the first variant that has a weight. Returns undefined when the product data has no variants
// loaded, so checkout fills it in later.
export const defaultVariantWeight = (product) => {
  const combos = product?.variant_combinations;
  if (!Array.isArray(combos)) return undefined;
  const key = product._variantExpanded ? product._variantKey?.toLowerCase() : null;
  const pick = (list) => list.find(c => toGrams(c?.weight) > 0);
  const own = key ? pick(combos.filter(c => c?.[key] === product._variantValue)) : null;
  return toGrams((own || pick(combos))?.weight) || 0;
};

// Quick add 1 unit of a product (no variant selection required)
export function quickAddToCart(product) {
  const cart = getCart();
  const id = product.id;
  const existing = cart.findIndex(i => i.id === id);
  if (existing >= 0) {
    const updated = cart.map((item, idx) => {
      if (idx !== existing) return item;
      const newQty = item.totalQty + 1;
      return {
        ...item,
        totalQty: newQty,
        variantLines: item.variantLines.map(l => ({ ...l, qty: l.qty + 1 })),
      };
    });
    saveCart(updated);
  } else {
    const item = {
      id,
      slug: product.url_slug || product.slug || id,
      name: product.item_name || product.name || "",
      image: product.main_image || product.image || "",
      price: product.deal_price_incl_gst || product.price || 0,
      mrp: product.mrp || product.deal_price_incl_gst || product.price || 0,
      rating: product.star_rating || 0,
      numReviews: product.num_reviews || 0,
      weight: toGrams(product.shipping_weight),
      variantLines: [{ key: "|", color: null, size: null, qty: 1, ...(defaultVariantWeight(product) !== undefined ? { weight: defaultVariantWeight(product) } : {}) }],
      totalQty: 1,
    };
    saveCart([...cart, item]);
  }
  return true;
}
